import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
    Link,
    styled,
    accordionClasses,
    accordionSummaryClasses,
    accordionDetailsClasses,
    Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import BlankDocument from 'assets/illustrations/tiny/blank-document.svg';
import { Attachment } from 'services/help-center/queries/useTicketComments/useTicketComments.interface';

interface AttachmentsAccordionProps {
    attachments: Attachment[];
}

const StyledAccordion = styled(Accordion)(() => ({
    [`&.${accordionClasses.root}`]: {
        background: 'transparent',
        boxShadow: 'none',
        '&:before': {
            display: 'none',
        },
    },
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
    [`&.${accordionSummaryClasses.root}`]: {
        padding: 0,
        margin: 0,
        marginTop: 1.5,
        minHeight: 'auto',
        [`.${accordionSummaryClasses.content}`]: {
            padding: 0,
            margin: 0,
        },
    },
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
    [`&.${accordionDetailsClasses.root}`]: {
        padding: 0,
        margin: 0,
        minHeight: 'auto',
    },
}));

export default function AttachmentsAccordion({ attachments }: AttachmentsAccordionProps) {
    if (attachments.length === 0) {
        return null;
    }

    return (
        <StyledAccordion sx={{ width: '100%' }}>
            <StyledAccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="attachments-content"
                id="attachments-header">
                <Typography variant="body2">Attachments</Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', paddingY: 2, gap: 2 }}>
                    {attachments.map((attachment) => (
                        <Box
                            key={attachment.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}>
                            <Tooltip title={attachment.file_name} placement="top">
                                <Link
                                    href={attachment.content_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={(theme) => ({
                                        display: 'block',
                                        width: 80,
                                        height: 80,
                                        border: `1px solid ${theme.palette.cement[300]}`,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                    })}>
                                    <Box
                                        component="img"
                                        src={attachment.thumbnails?.[0]?.content_url ?? BlankDocument}
                                        alt={attachment.file_name}
                                        sx={(theme) => ({
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            background: theme.palette.cement[50],
                                        })}
                                    />
                                </Link>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            </StyledAccordionDetails>
        </StyledAccordion>
    );
}
